// (c) chaehongee. Confidential and proprietary.

import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks'
import { useMaskActions } from '../../Canvas/MaskContext'
import { selectLorasState } from '../../../store/slices/lorasSlice'
import { addNode, mergeSessionEntities } from '../../../store/slices/sessionSlice'
import { setNodes as setSelectedNodes } from '../../../store/slices/selectionSlice'
import { useMaskWorkflow } from './useMaskWorkflow'
import { selectActiveSession, selectActiveSessionId } from '../../../store/selectors/sessionSelectors'
import { selectActiveSelection, selectActiveSelectedNodeIds } from '../../../store/selectors/selectionSelectors'

export interface CanvasContext {
    selectedNodeIds: string[]
    selectedLoraId: string | null
}

export function useCanvasContext() {
    const dispatch = useAppDispatch()
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const session = useAppSelector(selectActiveSession)
    const selectedNodeIds = useAppSelector(selectActiveSelectedNodeIds)
    const selection = useAppSelector(selectActiveSelection)
    const loras = useAppSelector(selectLorasState)
    const { exportFramedImageWithUpload, resetMask, setBrushEnabled } = useMaskActions()
    const { attachMaskToBase } = useMaskWorkflow()

    const getContext = useCallback(async (): Promise<CanvasContext> => {
        let currentSelectedNodes = [...selectedNodeIds]

        if (!session?.id || !activeSessionId) {
            return { selectedNodeIds: [], selectedLoraId: null }
        }

        // 1. Handle Framed Export
        try {
            if (exportFramedImageWithUpload) {
                const result = await exportFramedImageWithUpload()
                if (result) {
                    // The new function handles upload and commit creation internally
                    currentSelectedNodes = [result.node.id]
                    dispatch(addNode({ sessionId: activeSessionId, node: result.node }))
                    dispatch(mergeSessionEntities({ sessionId: activeSessionId, commits: [result.commit] }))
                    dispatch(setSelectedNodes({ sessionId: activeSessionId, nodeIds: [result.node.id] }))
                }
            }
        } catch (e) {
            console.warn('Failed to export framed image', e)
        }

        // 2. Handle Mask/Brush Context
        let selectedLoraId: string | null = null

        if (selection.brushEnabled) {
            const chosenLoraId = loras.selectedId || null
            if (selection.hasMaskStrokes && chosenLoraId) {
                const baseNodeId = currentSelectedNodes.length > 0
                    ? currentSelectedNodes[currentSelectedNodes.length - 1]
                    : undefined

                // Upload mask and link to base node
                const attachment = await attachMaskToBase({ baseNodeId })
                if (attachment) {
                    currentSelectedNodes = attachment.payloadNodeIds
                    selectedLoraId = chosenLoraId
                }
            }

            // Reset canvas state
            if (setBrushEnabled) setBrushEnabled(false)
            if (resetMask) resetMask()
        }

        return {
            selectedNodeIds: currentSelectedNodes,
            selectedLoraId
        }
    }, [
        session?.id,
        activeSessionId,
        selectedNodeIds,
        exportFramedImageWithUpload,
        dispatch,
        selection.brushEnabled,
        selection.hasMaskStrokes,
        loras.selectedId,
        attachMaskToBase,
        setBrushEnabled,
        resetMask
    ])

    return { getContext }
}

